import { RefObject, useEffect } from "react";

// The persistent consent tab is fixed to the bottom-left corner, where the
// quiz's sticky mobile controls also sit. Toggle a root class while the quiz
// owns that corner and let CSS hide the tab (see _base.scss) — the tab is
// injected by the consent vendor's script, outside React's tree, and may not
// exist in the DOM yet when the quiz mounts.
const ROOT_CLASS = "dx-quiz-hides-consent";

// A page can carry more than one quiz module, so track every claiming instance
// rather than letting the last observer callback win.
const claiming = new Set<Element>();

const sync = () => {
  document.documentElement.classList.toggle(ROOT_CLASS, claiming.size > 0);
};

// Enter and exit sit at different marks, and one observer can't straddle both:
// a single root band's top edge decides the exit and its bottom edge the entry.
// So run one observer per edge and require both.
//   - top 70% of the viewport: intersects once the quiz's top edge has scrolled
//     past the 30% mark, which is when the tab goes
//   - bottom 10%: stops intersecting once the quiz's bottom edge rises past the
//     10% mark, which is when the tab returns
const ENTER_MARGIN = "0px 0px -30% 0px";
const EXIT_MARGIN = "-90% 0px 0px 0px";

export function useHideConsentBadge(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let pastEnterMark = false;
    let aboveExitMark = false;

    const update = () => {
      if (pastEnterMark && aboveExitMark) claiming.add(el);
      else claiming.delete(el);
      sync();
    };

    const observe = (rootMargin: string, set: (hit: boolean) => void) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          set(entry.isIntersecting);
          update();
        },
        { rootMargin }
      );
      observer.observe(el);
      return observer;
    };

    const observers = [
      observe(ENTER_MARGIN, (hit) => { pastEnterMark = hit; }),
      observe(EXIT_MARGIN, (hit) => { aboveExitMark = hit; }),
    ];

    return () => {
      observers.forEach((observer) => observer.disconnect());
      claiming.delete(el);
      sync();
    };
  }, [ref]);
}
